import * as THREE from 'three';
import { cornerCam, flatCam, orbitCam, scene } from '../scene/context.js';
import { eyeVec, selBox } from '../scene/helpers.js';
import { getSel } from '../scene/objects.js';
import { $, S, clamp } from '../state/store.js';
import { siteFitK, siteQuadH } from '../ui/site-view.js';
import { isSiteLike } from '../scene/cameras.js';

/* ═══════════════════════════════════════════════════════════
   TRANSFORM GIZMO
   Hand-rolled rather than pulled from the addons bundle, so it can be styled
   to match and so it behaves predictably under the sheared off-axis frustum.
   Every handle is a Group carrying userData.gz plus an invisible fat picker,
   because a 15 mm cylinder is impossible to hit with a mouse.
   ═══════════════════════════════════════════════════════════ */
export const AX = {x:new THREE.Vector3(1,0,0), y:new THREE.Vector3(0,1,0), z:new THREE.Vector3(0,0,1)};
const GZC = {x:0xFF5C5C, y:0x6BE08A, z:0x5BA9D6, all:0xE8EDF1, hi:0xF2C300};

/* The gizmo lives in its own scene and is drawn in a second pass over a cleared
   depth buffer. Relying on renderOrder inside the main scene means any stray
   transparent material can sort in front of it; a separate pass cannot fail. */
export const gizmoScene = new THREE.Scene();
export const gizmo = new THREE.Group();
gizmo.visible = false;
gizmoScene.add(gizmo);
// The selection outline gets the same "always on top, unoccluded" treatment
// as the gizmo, for the same reason — so it stays visible behind whatever
// it's selecting. That means it also rides through the wide-margin frustum
// substitution scene/renderer.js's draw()/drawSplit() do for Flat/On-site
// (so an out-of-frame gizmo handle stays grabbable) — scaleSelBox() there
// compensates it the same way gizmo.scale already is, or it would render
// shrunk by the margin whenever that substitution is active.
scene.remove(selBox);
gizmoScene.add(selBox);
export const gzGroups = {};

/* A visual cue for a selected face-camera object: which way it will actually
   point once real billboarding kicks in. scene/renderer.js's applyBillboards
   deliberately does NOT rotate it in Orbit — spinning the object under you
   as you orbit would hide its real position — so without this there is no
   way to tell where "facing the camera" actually points while composing.
   Orbit-only: Flat/On-site already show the real, faced orientation
   directly, nothing to point out there. Lives in gizmoScene for the same
   "always on top, unoccluded" reason as the gizmo and selBox above. */
export const faceArrow = new THREE.ArrowHelper(new THREE.Vector3(0,0,1), new THREE.Vector3(), 1, 0x5BA9D6);
faceArrow.visible = false;
gizmoScene.add(faceArrow);

/* A label saying so in words, since the arrow alone only reads as "something
   points that way" once you already know what it means.

   It earns its place most in Rotate mode: updateGizmo below hides the gizmo
   outright for a face-camera object (its rotation is driven every frame by
   applyBillboards, so rotate handles would do nothing) — which without this
   is a gizmo that simply vanishes with no reason given. So the tag is a
   SIBLING of the gizmo rather than a child of it: it has to outlive the
   thing it explains.

   A sprite rather than a DOM overlay: sprites already face the viewer, and
   this way the label rides the same "always on top" gizmo pass as everything
   else here instead of needing its own per-frame screen-position sync
   against four different view layouts.

   Drawn to match .chip in styles/app.css — the app's own small-label idiom:
   a pill at --panel2 behind a hairline --line border, muted text at a
   modest weight, and a small accent dot. Deliberately quiet: it labels a
   state, it isn't a warning, and it sits over the user's artwork. The
   canvas is rendered at 3x the nominal size purely for texture sharpness;
   what governs its size on screen is the scale set in updateFaceArrow. */
const TAG_TEXT = 'Faces camera';
function makeTag(text){
  const SS = 3;                                  // supersample, for a crisp texture
  const fontPx = 11, padX = 9, padY = 4, dot = 5, gap = 6, radius = 100;
  const probe = document.createElement('canvas').getContext('2d');
  const font = `500 ${fontPx}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
  probe.font = font;
  const textW = Math.ceil(probe.measureText(text).width);
  const w = padX * 2 + dot + gap + textW;
  const h = fontPx + padY * 2 + 4;

  const cv = document.createElement('canvas');
  cv.width = Math.round(w * SS); cv.height = Math.round(h * SS);
  const c = cv.getContext('2d');
  c.scale(SS, SS);
  c.font = font;
  c.textBaseline = 'middle';

  const r = Math.min(radius, h / 2);
  c.beginPath();
  c.moveTo(r, 0); c.lineTo(w - r, 0); c.arc(w - r, r, r, -Math.PI/2, Math.PI/2);
  c.lineTo(r, h); c.arc(r, r, r, Math.PI/2, -Math.PI/2); c.closePath();
  c.fillStyle = 'rgba(35,38,41,.92)';            // --panel2, near-opaque so it reads over any artwork
  c.fill();
  c.lineWidth = 1; c.strokeStyle = 'rgba(255,255,255,.16)';   // --line, dark theme
  c.stroke();

  // The accent dot .chip uses, in the same blue as the face-camera arrow.
  c.beginPath();
  c.arc(padX + dot/2, h/2, dot/2, 0, Math.PI*2);
  c.fillStyle = '#5BA9D6';
  c.fill();

  c.fillStyle = '#C6CDD4';                       // --muted (a canvas cannot read the variable)
  c.fillText(text, padX + dot + gap, h/2 + .5);

  const tex = new THREE.CanvasTexture(cv);
  tex.anisotropy = 4;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: tex, transparent: true, depthTest: false, depthWrite: false, toneMapped: false
  }));
  sprite.userData.aspect = w / h;
  sprite.renderOrder = 1003;
  sprite.visible = false;
  return sprite;
}
export const faceTag = makeTag(TAG_TEXT);
gizmoScene.add(faceTag);
/* Same treatment for a masked image. A mask is invisible in Orbit — the
   polygon only clips the plane in the flat/on-site render — so a selected
   object can look untouched while its deliverable is cropped, and there is
   nothing on the gizmo to say why. Whether it is inverted stays in the
   inspector; the tag answers "is anything hidden here", which is the question
   you have while looking at the scene. */
export const maskTag = makeTag('Masked');
gizmoScene.add(maskTag);

/* Does the gizmo pass have anything to draw?
   Lives here, next to the things it lists, because scene/renderer.js asked
   the same question in three places by naming each object — so adding this
   tag meant a masked object in Rotate mode (where updateGizmo hides the
   gizmo) would have had its pass skipped and its tag silently never drawn.
   One function, three call sites, and the next thing added to this scene
   only has to be remembered once. */
export const gizmoLayerVisible = () =>
  gizmo.visible || selBox.visible || faceArrow.visible || faceTag.visible || maskTag.visible;

const PICKMAT = new THREE.MeshBasicMaterial({
  transparent:true, opacity:0, depthTest:false, depthWrite:false, side:THREE.DoubleSide
});
/* Depth testing stays ON: inside the gizmo pass the buffer is freshly cleared,
   so handles occlude each other properly without ever hiding behind the scene. */
const gzMat = (c,op)=>new THREE.MeshBasicMaterial({
  color:c, transparent: op!=null && op<1, opacity: op==null?1:op,
  depthTest:true, depthWrite: op==null||op>=1,
  toneMapped:false, side:THREE.DoubleSide
});
const orient = (m,a)=>{ if(a==='x') m.rotation.z=-Math.PI/2; else if(a==='z') m.rotation.x=Math.PI/2; return m; };
const along  = (m,a,d)=>{ m.position.copy(AX[a]).multiplyScalar(d); return m; };

function mkHandle(gz, visuals, picker){
  const g = new THREE.Group();
  g.userData.gz = gz;
  g.userData.visuals = visuals;
  visuals.forEach(m=>{ m.renderOrder = 1002; g.add(m); });
  picker.material = PICKMAT;
  picker.renderOrder = 1001;
  g.add(picker);
  return g;
}

const ROT_R = .88;   // rotate-ring radius, local gizmo units (also used by pickRotateAxis below)

/* Picker geometry is fatter than what's drawn on purpose — see the file
   header — sized generously enough for a mouse. A trackpad's effective
   pointing precision is worse (no equivalent of a mouse's fixed, high-DPI
   sensor under your fingertip), so every picker here is wider again than
   that original mouse-only sizing: axis cylinders .13→.17, the uniform-
   move/scale pickers proportionally too, and the rotate ring's own 2D
   pick threshold below gets the same treatment. */
/* ONE DEFINITION OF A MOVE ARROW, because two things draw it now: the gizmo
   itself, and the Calibrate-camera rig's dial (ui/camrig.js), which renders
   these very meshes in a small canvas of its own so that "the rig's arrows
   look like the gizmo's" is true by construction rather than by somebody
   keeping two drawings in step. Exported rather than copied for exactly that
   reason — the rig previously drew flat SVG lookalikes, and the note left
   behind explaining why they could not match ("those arrows are lit") was not
   even right: gzMat is MeshBasicMaterial, unlit. What makes a gizmo arrow read
   as an arrow is its silhouette in perspective, which is free once you render
   the actual geometry.

   `c` is a parameter so the rig can build a lit-yellow copy for its highlight
   the same way the gizmo repaints to GZC.hi. */
export function moveArrowMeshes(a, c = GZC[a]){
  return [
    along(orient(new THREE.Mesh(new THREE.CylinderGeometry(.022,.022,.72,12), gzMat(c)), a), a, .37),
    along(orient(new THREE.Mesh(new THREE.ConeGeometry(.078,.24,20), gzMat(c)), a), a, .845)
  ];
}
/* The gizmo's own centre knob, for the same reason. */
export const moveHubMesh = () =>
  new THREE.Mesh(new THREE.SphereGeometry(.088,20,14), gzMat(GZC.all,.85));
export const GIZMO_COLOURS = GZC;

function buildGizmo(){
  /* ── move ── */
  const mv = new THREE.Group();
  ['x','y','z'].forEach(a=>{
    mv.add(mkHandle({mode:'move',kind:'axis',axis:a}, moveArrowMeshes(a),
      along(orient(new THREE.Mesh(new THREE.CylinderGeometry(.17,.17,1,8)), a), a, .5)));
  });
  [['z',[.40,.40,0],[0,0,0]],
   ['y',[.40,0,.40],[-Math.PI/2,0,0]],
   ['x',[0,.40,.40],[0,Math.PI/2,0]]].forEach(([n,p,r])=>{
    const q  = new THREE.Mesh(new THREE.PlaneGeometry(.26,.26), gzMat(GZC[n],.42));
    const pk = new THREE.Mesh(new THREE.PlaneGeometry(.38,.38));
    q.position.set(...p);  q.rotation.set(...r);
    pk.position.set(...p); pk.rotation.set(...r);
    mv.add(mkHandle({mode:'move',kind:'plane',axis:n}, [q], pk));
  });
  mv.add(mkHandle({mode:'move',kind:'screen'}, [moveHubMesh()],
    new THREE.Mesh(new THREE.SphereGeometry(.19,12,8))));
  gzGroups.move = mv;

  /* ── rotate ── */
  const rt = new THREE.Group();
  ['x','y','z'].forEach(a=>{
    const ring = new THREE.Mesh(new THREE.TorusGeometry(ROT_R,.019,10,110), gzMat(GZC[a]));
    const pk   = new THREE.Mesh(new THREE.TorusGeometry(ROT_R,.10,6,54));
    if(a==='x'){ ring.rotation.y = Math.PI/2; pk.rotation.y = Math.PI/2; }
    if(a==='y'){ ring.rotation.x = Math.PI/2; pk.rotation.x = Math.PI/2; }
    rt.add(mkHandle({mode:'rotate',kind:'axis',axis:a}, [ring], pk));
  });
  gzGroups.rotate = rt;

  /* ── scale ── */
  const sc = new THREE.Group();
  ['x','y','z'].forEach(a=>{
    const c = GZC[a];
    sc.add(mkHandle({mode:'scale',kind:'axis',axis:a}, [
      along(orient(new THREE.Mesh(new THREE.CylinderGeometry(.020,.020,.79,12), gzMat(c)), a), a, .40),
      along(new THREE.Mesh(new THREE.BoxGeometry(.13,.13,.13), gzMat(c)), a, .855)
    ], along(orient(new THREE.Mesh(new THREE.CylinderGeometry(.17,.17,1,8)), a), a, .5)));
  });
  sc.add(mkHandle({mode:'scale',kind:'uniform'},
    [new THREE.Mesh(new THREE.BoxGeometry(.16,.16,.16), gzMat(GZC.all,.9))],
    new THREE.Mesh(new THREE.BoxGeometry(.31,.31,.31))));
  gzGroups.scale = sc;

  Object.values(gzGroups).forEach(g=>gizmo.add(g));
}
buildGizmo();

/* Perpendicular distance from p to the segment a–b, clamped to the segment. */
function distToSeg(px,py, ax,ay, bx,by){
  const dx=bx-ax, dy=by-ay, lenSq=dx*dx+dy*dy;
  const t = lenSq>1e-12 ? Math.max(0,Math.min(1,((px-ax)*dx+(py-ay)*dy)/lenSq)) : 0;
  return Math.hypot(px-(ax+t*dx), py-(ay+t*dy));
}
const ROT_SAMPLES = 96;
const _rv = new THREE.Vector3();
/* The three rotate rings are concentric circles that overlap heavily in
   screen space, and their fat invisible 3D pickers overlap in raycast depth
   order too — the ray often hits a DIFFERENT ring's picker than the one
   that's visually closest to the cursor, especially near where two rings
   cross. Projecting each ring's true silhouette to NDC and picking by 2D
   distance to the pointer instead matches what the eye (and mouse) agree
   on, and degrades gracefully for a ring seen edge-on (falls back to a
   fixed minimum grab width rather than shrinking to an unpickable line). */
export function pickRotateAxis(ndcX, ndcY, cam){
  if(!gizmo.visible || !gzGroups.rotate.visible) return null;
  let best = null, bestD = Infinity;
  for(const group of gzGroups.rotate.children){
    const axis = group.userData.gz.axis;
    const pts = [];
    let cx=0, cy=0;
    for(let i=0;i<ROT_SAMPLES;i++){
      const t = (i/ROT_SAMPLES)*Math.PI*2;
      if(axis==='z') _rv.set(Math.cos(t)*ROT_R, Math.sin(t)*ROT_R, 0);
      else if(axis==='x') _rv.set(0, Math.sin(t)*ROT_R, Math.cos(t)*ROT_R);
      else _rv.set(Math.cos(t)*ROT_R, 0, Math.sin(t)*ROT_R);
      _rv.applyMatrix4(gizmo.matrixWorld).project(cam);
      pts.push({x:_rv.x, y:_rv.y});
      cx += _rv.x; cy += _rv.y;
    }
    cx /= ROT_SAMPLES; cy /= ROT_SAMPLES;
    let maxR = 0;
    for(const p of pts) maxR = Math.max(maxR, Math.hypot(p.x-cx, p.y-cy));
    const thresh = Math.max(maxR*0.22, .018);
    for(let i=0;i<pts.length;i++){
      const a = pts[i], b = pts[(i+1)%pts.length];
      const d = distToSeg(ndcX, ndcY, a.x, a.y, b.x, b.y);
      if(d < thresh && d < bestD){ bestD = d; best = group; }
    }
  }
  return best;
}

/* Scale is applied in the node's own local frame, so a world-aligned scale gizmo
   on a rotated object would need shear. Scale mode is therefore always local. */
export const gzLocal = ()=>S.gizmoSpace==='local' || S.gizmo==='scale';

/* A fixed CSS-pixel target, not a fraction of the current viewport — a
   fraction reads as "weirdly" different sizes depending on how big the
   browser window currently happens to be (a maximized window and a tiled
   half-screen one show the same fraction at very different physical sizes);
   a fixed pixel target keeps the handle the same physical size regardless,
   the way toolbar icons and cursors already are. Orbit and Flat render
   straight to what's on screen, so this targets pixels directly against
   the on-screen canvas's own current size (#fitbox, or #fitboxSplitR for
   Split's right/flat pane — read straight from the DOM rather than from
   the renderer's own state, which updateGizmo's own call sites set AFTER
   calling it and so can't be trusted to reflect the pane about to render).
   On-site is different again: what's on screen is a homography-warped
   composite (see blitSiteGizmo), not the raw render, so it targets the
   same pixel count against the screen quad's actual on-screen size
   (siteQuadH) instead. */
export const GIZMO_TARGET_PX = 110;     // CSS pixels tall on screen, at gizmoSize 1

function gizmoCanvasHeightPx(cam){
  /* Which pane this camera draws into. Split's flat preview and a corner
     screen's second panel are each a separate box with its own height, and the
     gizmo is sized as a fraction of that height. */
  /* In Split WITH a photograph the right pane is the mockup, not the flat
     preview — #fitboxSplitR is stood down and measures zero, which would make
     the gizmo the whole height of nothing. */
  const siteSplit = S.view === 'split' && !!S.photo;
  const el = (cam === flatCam && S.view === 'split')
             ? (siteSplit ? $('#siteWrap') : $('#fitboxSplitR'))
           : (cam === cornerCam) ? $('#fitboxCornerR')
           : $('#fitbox');
  return (el && el.clientHeight) || ($('#stage') || {}).clientHeight || 800;
}

function gizmoScreenFrac(cam){
  const targetPx = GIZMO_TARGET_PX * S.gizmoSize;
  const base = targetPx / Math.max(1, gizmoCanvasHeightPx(cam));
  /* Not for the ORBIT camera. This correction is for handles drawn through the
     photograph's homography, and in Split the orbit pane is on screen at the
     same time as the mockup — sized by the warp it is not drawn through, its
     gizmo came out several times too big. */
  if(!isSiteLike() || cam === orbitCam || siteQuadH <= 0) return base;
  /* on screen the gizmo covers frac * siteQuadH * zoom pixels; solve for frac.
     Overscan contributes two factors and BOTH are needed: the quad is the
     middle 1/m of the canvas, so a given fraction of the canvas draws m×
     larger — and the composite is then shrunk by siteFitK to fit the widened
     result into the view, which takes it back down again. Dividing by only
     the first left the handles roughly a third of their proper size and too
     fine to hit, which read as "the gizmo is drawn but cannot be grabbed" —
     the exact symptom this feature exists to remove. */
  const over = Math.max(1, S.siteOverscan || 1);
  const want = targetPx / Math.max(1, siteQuadH * (S.siteZoom || 1) * over * (siteFitK || 1));
  // .8 covers a screen quad down to about 15% of the stage; below that the gizmo
  // would start clipping against the edge of the render, so it stops growing
  return clamp(want, base, .8);
}

/* How far the eye stands off a panel, measured along that panel's own normal.
   Identical to eyeVec().z for the single screen, which sits at z=0 unrotated. */
const _pe = new THREE.Vector3(), _pq = new THREE.Quaternion();
function panelEyeZ(desc){
  return _pe.copy(eyeVec()).sub(desc.pos).applyQuaternion(_pq.copy(desc.quat).invert()).z;
}

/* Keep the gizmo a constant fraction of viewport height at any depth. */
export function gizmoWorldSize(cam, p){
  const fwd = new THREE.Vector3(0,0,-1).applyQuaternion(cam.quaternion);
  const d = Math.max(1e-3, p.clone().sub(cam.position).dot(fwd));
  /* Which panel this camera is looking through, if any — stamped by
     updateScreenCam (scene/cameras.js), so it is always current and needs no
     name check here. It used to test `cam === flatCam`, which already had to
     know about Split and would have needed a fresh arm for every panel added.
     The eye's distance has to be measured in the PANEL's frame too: for the
     single screen that is the world z it always was, but a panel turned to face
     along +X would otherwise be sized off a distance that means nothing to it. */
  const desc = cam.userData.screenDesc;
  const vh = desc
    ? desc.h * d / Math.max(1e-4, panelEyeZ(desc))     // sheared frustum
    : 2*d*Math.tan(cam.fov*Math.PI/360);               // ordinary perspective
  return clamp(vh*gizmoScreenFrac(cam), 1e-5, 1e7);
}

/* WHERE AN OBJECT REALLY IS. A linked child's pos/rot/scale are relative to its
   master (scene/objects.js's linking), so reading o.pos as a world position put
   its handles at the child's OFFSET from the master — near the origin after
   "Parent to a new null" — while the object itself sat out at the null.
   Everything the handles do is in world space, so they go through this.

   parent: how a WORLD movement becomes a movement in the object's own frame —
   the master's world rotation and scale, identity for an object with none. */
const _wpos = new THREE.Vector3(), _wquat = new THREE.Quaternion(), _pquat = new THREE.Quaternion(), _pscale = new THREE.Vector3();
export function worldPose(o){
  const pos = new THREE.Vector3(o.pos.x, o.pos.y, o.pos.z);
  const quat = objQuat(o);
  const parentQuat = new THREE.Quaternion(), parentScale = new THREE.Vector3(1, 1, 1);
  const pn = o.parentId != null && o.node && o.node.parent && o.node.parent.userData.objId != null ? o.node.parent : null;
  if(pn){
    o.node.updateWorldMatrix(true, false);
    o.node.getWorldPosition(_wpos); pos.copy(_wpos);
    o.node.getWorldQuaternion(_wquat); quat.copy(_wquat);
    pn.getWorldQuaternion(_pquat); parentQuat.copy(_pquat);
    pn.getWorldScale(_pscale); parentScale.copy(_pscale);
  }
  return { pos, quat, parentQuat, parentScale };
}
/** A world-space movement, in the object's own (parent-relative) frame. */
export function worldDeltaToLocal(o, d){
  const { parentQuat, parentScale } = worldPose(o);
  const v = d.clone().applyQuaternion(parentQuat.clone().invert());
  v.x /= parentScale.x || 1; v.y /= parentScale.y || 1; v.z /= parentScale.z || 1;
  return v;
}

export function objQuat(o){
  return new THREE.Quaternion().setFromEuler(
    new THREE.Euler(o.rot.x*Math.PI/180, o.rot.y*Math.PI/180, o.rot.z*Math.PI/180));
}

export function setLightViz(on){
  S.objects.forEach(ob=>{
    if(ob.type!=='light' || !ob.node) return;
    ob.node.children.forEach(c=>{ if(c.userData.lightViz) c.visible = on; });
  });
}

/* The direction indicator (cone/rings — see scene/objects.js's
   buildLightViz) is tagged separately from the bulb it sits alongside, so
   it can be gated on selection independently: every light's bulb stays
   visible (still a findable/clickable handle), but the cone/rings only
   draw for whichever one is actually selected, not all of them at once.
   Runs after setLightViz(true) in scene/renderer.js's prepDraw — that call
   makes the whole viz group visible again each frame, this narrows the
   direction indicator specifically back down to just the selection. */
export function updateLightVizSelection(){
  const sel = getSel();
  // Flat and On-site are both previews of the finished deliverable — the
  // bulb is a composing aid, not something that belongs in what's supposed
  // to read as the actual export, so unlike Orbit (a free composing view,
  // where every bulb stays visible as a findable handle), both only show
  // the bulb for whichever light is actually selected.
  const bulbFollowsSelection = isSiteLike() || S.view === 'flat';
  S.objects.forEach(ob=>{
    if(ob.type!=='light' || !ob.node) return;
    const on = ob === sel;
    if(bulbFollowsSelection) ob.node.children.forEach(c=>{ if(c.userData.lightViz) c.visible = on; });
    ob.node.traverse(c=>{ if(c.userData.lightVizDirection) c.visible = on; });
  });
}

export function updateGizmo(cam){
  const o = getSel();
  // The eye/camera "selected" instead of a real object — move only (it
  // has no rotation, off-axis projections never turn the viewer, and no
  // scale either), driving S.nudge rather than a real o.pos. See
  // ui/interaction.js's startGizmo/moveGizmo for the write side.
  if(!o && S.eyeSelected && S.gizmo==='move' && S.stage!=='calib'){
    const p = eyeVec();
    gizmo.visible = true;
    for(const k in gzGroups) gzGroups[k].visible = (k === 'move');
    gizmo.position.copy(p);
    gizmo.quaternion.identity();   // never local space — there's no rotation to align to
    gizmo.scale.setScalar(gizmoWorldSize(cam, p));
    gizmo.updateMatrixWorld(true);
    return;
  }
  // rotating a billboarded object does nothing, so hide the rotate gizmo for it
  if(o && o.faceCamera && S.gizmo==='rotate'){ gizmo.visible=false; return; }
  // a frame's (and a recess's) transform is fixed — nothing to grab, so no handles to draw
  const show = !!o && o.visible && o.type!=='frame' && o.type!=='recess' && S.stage!=='calib';
  gizmo.visible = show;
  if(!show) return;
  for(const k in gzGroups) gzGroups[k].visible = (k === S.gizmo);
  const wp = worldPose(o);
  const p = wp.pos;
  gizmo.position.copy(p);
  if(gzLocal()) gizmo.quaternion.copy(wp.quat);
  else gizmo.quaternion.identity();
  gizmo.scale.setScalar(gizmoWorldSize(cam, p));
  gizmo.updateMatrixWorld(true);
}

/* See faceArrow's own comment above for why this exists. Orbit-only — cam
   is orbitCam for standalone Orbit and for Split's own orbit/left pane
   alike, so this covers both without any view-name check. */
export function updateFaceArrow(cam){
  const o = getSel();
  const faced = !!o && o.faceCamera && o.visible && S.stage !== 'calib';
  const show = cam === orbitCam && faced;
  faceArrow.visible = show;

  /* The tag, unlike the arrow, is NOT Orbit-only. The arrow points out where
     an object will end up facing, which only means anything in a view that
     isn't already showing it faced — but "this object is on face-camera" is
     worth stating wherever the object is selected, and it's the only
     explanation on offer in Rotate mode, where the gizmo hides itself. */
  /* Masked follows exactly the same rules, and for the same reason: it is a
     state of the object that the viewport does not show. */
  /* Matches what scene/objects.js actually clips with, not just the flag:
     a mask needs three points before it cuts anything, so `on` alone could
     put a tag on an object whose render is untouched. Deliberately ignores
     the transient _maskEdit, which unmasks the plane only while the editor
     is open and over the top of everything anyway. */
  const mk = o && o.mask;
  const masked = !!o && !!(mk && mk.on && mk.points && mk.points.length >= 3)
    && o.visible && S.stage !== 'calib';
  faceTag.visible = faced;
  maskTag.visible = masked;

  if(faced || masked){
    const p = new THREE.Vector3(o.pos.x, o.pos.y, o.pos.z);
    const unit = gizmoWorldSize(cam, p);
    const h = unit * .17;                    // constant apparent size, like the gizmo
    // Clear of the gizmo's own handles, which reach out by roughly one unit.
    const base = p.y + unit * 1.2;

    if(faced){
      faceTag.scale.set(h * faceTag.userData.aspect, h, 1);
      faceTag.position.set(p.x, base, p.z);
    }
    if(masked){
      maskTag.scale.set(h * maskTag.userData.aspect, h, 1);
      /* Stacked above the face tag when both apply, rather than both landing
         on the same point — an image can be masked AND face the camera, and
         two sprites at one position render as one unreadable smear. */
      maskTag.position.set(p.x, base + (faced ? h * 1.25 : 0), p.z);
    }
  }

  if(!show) return;
  const p = new THREE.Vector3(o.pos.x, o.pos.y, o.pos.z);
  const dir = eyeVec().clone().sub(p);
  if(dir.lengthSq() < 1e-8){ faceArrow.visible = false; return; }
  dir.normalize();
  const len = gizmoWorldSize(cam, p) * .8;   // constant apparent size, matching the gizmo
  faceArrow.position.copy(p);
  faceArrow.setDirection(dir);
  faceArrow.setLength(len, len * .3, len * .16);
}

function paintHandle(node, on){
  const base = node.userData.gz.axis ? GZC[node.userData.gz.axis] : GZC.all;
  node.userData.visuals.forEach(m=>m.material.color.setHex(on ? GZC.hi : base));
}
let gzHover = null;
export function setHover(node){
  if(gzHover === node) return false;
  if(gzHover) paintHandle(gzHover, false);
  gzHover = node;
  if(gzHover) paintHandle(gzHover, true);
  return true;
}
